import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { Form, useForm, Controller } from 'react-hook-form';
import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, Flex } from '@chakra-ui/react';

import { Condition } from 'Component/others';
import { formUpsertText } from 'Const/studio/text';
import { CloseIcon } from 'Asset/icon';
import { TextBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputComponent from 'UiKit/input';
import InputSelectComponent from 'UiKit/input-select';
import InputTextAreaComponent from 'UiKit/input-textarea';

import usePostStudio from 'Domain/studio/usePostStudio';
import usePatchStudio from 'Domain/studio/usePatchStudio';
import useGetApplicationType from 'Domain/studio/useGetApplicationType';
import useGetArea from 'Domain/studio/useGetArea';
import usePostStudioAvailability from 'Domain/studio/usePostStudioAvailability';
import { useDebounce } from 'Helper/debounce';

import { useStudioState } from 'View/studio/state';
import useCloseModalRestriction from 'View/studio/hooks/useCloseModalRestriction';
import populateFormFields from 'View/studio/helper/populateFormFields';
import checkInlineValidation from 'View/studio/helper/checkInlineValidation';
import checkFormUpsertValidation from 'View/studio/helper/checkFormUpsertValidation';

import { defaultValues } from './const';
import styles from './styles.module.css';

const FormUpsert = () => {
  const { state, updateState } = useStudioState();
  const { control, register, watch, reset, formState, handleSubmit, setValue, clearErrors, setError, resetField } = useForm({ defaultValues });

  const { data: appTypeOptions, isLoading: isLoadingAppTypes } = useGetApplicationType();
  const { data: areaOptions, isLoading: isLoadingAreas } = useGetArea();
  const { mutate: createStudio, isPending: isCreating } = usePostStudio(reset);
  const { mutate: updateStudio, isPending: isUpdating } = usePatchStudio(reset);
  const { mutate: checkStudioAvailablity, isPending: isLoadingInlineVerification } = usePostStudioAvailability(setError);

  const { onCloseRestriction } = useCloseModalRestriction();

  const studioValidationHandler = useDebounce(checkStudioAvailablity, 300);

  const isEditFormUpsert = !isEmpty(state?.selectedStudio);
  const formField = watch();
  const { errors } = formState;

  const isLoading = isCreating || isUpdating;

  const isValidForm = checkFormUpsertValidation({ formField, errors });

  const onClose = () => {
    onCloseRestriction({
      submit: () => {
        updateState({ isOpenFormUpsert: false, selectedStudio: null });
        reset();
      },
    });
  };

  const onSubmit = (data) => {
    if (isEditFormUpsert) updateStudio({ data, id: state?.selectedStudio.id });
    else createStudio(data);
  };

  const onChangeInlineField = (event) => {
    const value = event.target.value;

    clearErrors('name');

    const isInlineValidation = checkInlineValidation(value, state?.selectedStudio);

    if (isInlineValidation) studioValidationHandler({ name: value });

    setValue('name', value);
  };

  useEffect(() => {
    if (isEditFormUpsert) populateFormFields({ setValue, selectedStudio: state?.selectedStudio });
  }, [isEditFormUpsert, state?.selectedStudio, setValue]);

  return (
    <Modal isOpen={state?.isOpenFormUpsert} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxWidth={'581px'} paddingBottom="16px">
        <ModalHeader className={styles['form__header']}>
          <TextBold className={styles['form__title-text']}>
            <Condition statement={isEditFormUpsert} fallback={'Create '}>
              Edit{' '}
            </Condition>
            Studio
          </TextBold>

          <CloseIcon color="var(--color-semantic-text)" size="22px" onClick={onClose} />
        </ModalHeader>

        <ModalBody className={styles['form__container']}>
          <Form control={control} onSubmit={handleSubmit(onSubmit)}>
            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'room'}
                rules={{ required: 'Room is required' }}
                render={({ field }) => (
                  <InputComponent
                    className={styles['form__input']}
                    isRequired
                    value={formField?.room}
                    label={formUpsertText.roomLabel}
                    placeholder={formUpsertText.roomPlaceholder}
                    field={field}
                    error={errors.room}
                    register={{ ...register('room') }}
                  />
                )}
              />
            </Box>

            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'name'}
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <InputComponent
                    className={styles['form__input']}
                    isRequired
                    value={formField?.name}
                    label={formUpsertText.nameLabel}
                    placeholder={formUpsertText.namePlaceholder}
                    field={field}
                    isLoading={isLoadingInlineVerification}
                    error={errors.name}
                    register={{ ...register('name'), onChange: onChangeInlineField }}
                    isReset={errors.name}
                    handleReset={() => resetField('name')}
                  />
                )}
              />
            </Box>

            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'address'}
                rules={{ required: 'Address is required' }}
                render={({ field }) => (
                  <InputTextAreaComponent
                    className={styles['form__input']}
                    isRequired
                    value={formField?.address}
                    label={formUpsertText.addressLabel}
                    placeholder={formUpsertText.addressPlaceholder}
                    field={field}
                    error={errors.address}
                    register={{ ...register('address') }}
                  />
                )}
              />
            </Box>

            {/* Lo-Fi Master Data */}
            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'latitude'}
                rules={{ required: 'Latitude is required' }}
                render={({ field }) => (
                  <InputComponent
                    className={styles['form__input']}
                    isRequired
                    value={formField?.latitude}
                    label={formUpsertText.latitudeLabel}
                    placeholder={formUpsertText.latitudePlacholder}
                    type="number"
                    field={field}
                    error={errors.latitude}
                    register={{ ...register('latitude', { pattern: { value: /^-?\d+(\.\d+)?$/, message: 'Latitude must be a number' } }) }}
                  />
                )}
              />
            </Box>

            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'longitude'}
                rules={{ required: 'Longitude is required' }}
                render={({ field }) => (
                  <InputComponent
                    className={styles['form__input']}
                    isRequired
                    value={formField?.longitude}
                    label={formUpsertText.longitudeLabel}
                    placeholder={formUpsertText.longitudePlaceholder}
                    type="number"
                    field={field}
                    isLoading={isLoadingInlineVerification}
                    error={errors.longitude}
                    register={{ ...register('longitude', { pattern: { value: /^-?\d+(\.\d+)?$/, message: 'Longitude must be a number' } }) }}
                  />
                )}
              />
            </Box>
            {/* Lo-Fi Master Data */}

            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'appType'}
                rules={{ required: 'App Type is required' }}
                render={({ field }) => (
                  <InputSelectComponent
                    className={styles['form__input']}
                    options={appTypeOptions}
                    isRequired
                    value={formField?.appType}
                    label={formUpsertText.appTypeLabel}
                    placeholder={formUpsertText.appTypePlaceholder}
                    field={field}
                    error={errors.appType}
                    register={{ ...register('appType') }}
                    isLoading={isLoadingAppTypes}
                  />
                )}
              />
            </Box>

            <Box className={styles['field__item']}>
              <Controller
                control={control}
                name={'area'}
                rules={{ required: 'Area is required' }}
                render={({ field }) => (
                  <InputSelectComponent
                    className={styles['form__input']}
                    options={areaOptions}
                    isRequired
                    value={formField?.area}
                    label={formUpsertText.areaLabel}
                    placeholder={formUpsertText.areaPlaceholder}
                    field={field}
                    error={errors.area}
                    register={{ ...register('area') }}
                    isLoading={isLoadingAreas}
                  />
                )}
              />
            </Box>

            <Flex gap={'24px'} flexDirection={'column'}>
              <Flex gap={'21px'}>
                <ButtonOutline type="button" width={'50%'} className={styles['form__button--cancel']} color="#000" onClick={onClose} isDisabled={isLoading}>
                  Batal
                </ButtonOutline>
                <ButtonSolid type="submit" width={'50%'} className={styles['form__button--submit']} isLoading={isLoading} isDisabled={!isValidForm}>
                  Simpan
                </ButtonSolid>
              </Flex>
            </Flex>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FormUpsert;
